import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  createJobApplication, 
  validateJobApplication, 
  APPLICATION_STATUS 
} from '../types/jobapplication';
import { cloudinaryService } from './cloudinaryService';

// Collection name for job applications
const APPLICATIONS_COLLECTION = 'jobApplications';

/**
 * Service for handling job application operations
 */
export const applicationServices = {
  /**
   * Submit a new job application
   * @param {Object} applicationData - Application data from form
   * @param {File} resumeFile - Resume PDF file
   * @param {Object} user - Current user object from Firebase Auth
   * @returns {Promise<{success: boolean, applicationId: string, error: string}>}
   */
  async submitApplication(applicationData, resumeFile, user) {
    try {
      // Validate user is authenticated
      if (!user || !user.uid) {
        throw new Error('User must be authenticated to submit an application');
      }

      // Upload resume file to Cloudinary
      const uploadResult = await cloudinaryService.uploadResume(resumeFile, user.uid);
      
      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }

      // Create application object
      const application = createJobApplication({
        ...applicationData,
        resumeUrl: uploadResult.secure_url,
        resumeFileName: resumeFile.name,
        resumePublicId: uploadResult.public_id, // Store Cloudinary public_id for future management
        email: user.email,
        userId: user.uid
      });

      // Validate application
      const validation = validateJobApplication(application);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Add to Firestore with server timestamp
      const docRef = await addDoc(collection(db, APPLICATIONS_COLLECTION), {
        ...application,
        appliedAt: serverTimestamp(),
        status: APPLICATION_STATUS.PENDING
      });

      return {
        success: true,
        applicationId: docRef.id
      };

    } catch (error) {
      console.error('Error submitting application:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get all applications for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} - List of user's applications
   */
  async getUserApplications(userId) {
    try {
      const q = query(
        collection(db, APPLICATIONS_COLLECTION),
        where('userId', '==', userId),
        orderBy('appliedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const applications = [];

      querySnapshot.forEach((doc) => {
        applications.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return applications;

    } catch (error) {
      console.error('Error getting user applications:', error);
      throw new Error(`Failed to get applications: ${error.message}`);
    }
  },

  /**
   * Get all applications (Admin only)
   * @returns {Promise<Array>} - List of all applications
   */
  async getAllApplications() {
    try {
      const q = query(
        collection(db, APPLICATIONS_COLLECTION),
        orderBy('appliedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const applications = [];

      querySnapshot.forEach((doc) => {
        applications.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return applications;

    } catch (error) {
      console.error('Error getting all applications:', error);
      throw new Error(`Failed to get applications: ${error.message}`);
    }
  },

  /**
   * Get a specific application by ID
   * @param {string} applicationId - Application document ID
   * @returns {Promise<Object>} - Application data
   */
  async getApplicationById(applicationId) {
    try {
      const docRef = doc(db, APPLICATIONS_COLLECTION, applicationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Application not found');
      }

    } catch (error) {
      console.error('Error getting application:', error);
      throw new Error(`Failed to get application: ${error.message}`);
    }
  },

  /**
   * Update application status (Admin function)
   * @param {string} applicationId - Application document ID
   * @param {string} status - New status
   * @param {string} notes - Optional notes
   * @returns {Promise<{success: boolean, error: string}>}
   */
  async updateApplicationStatus(applicationId, status, notes = '') {
    try {
      // Validate status
      const validStatuses = Object.values(APPLICATION_STATUS);
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
      }

      const docRef = doc(db, APPLICATIONS_COLLECTION, applicationId);
      await updateDoc(docRef, {
        status,
        notes: notes.trim(),
        updatedAt: serverTimestamp()
      });

      return { success: true };

    } catch (error) {
      console.error('Error updating application status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Delete an application and its associated resume from Cloudinary
   * @param {string} applicationId - Application document ID
   * @returns {Promise<{success: boolean, error: string}>}
   */
  async deleteApplication(applicationId) {
    try {
      // Get the application first to get the Cloudinary public_id
      const application = await this.getApplicationById(applicationId);
      
      // Delete resume from Cloudinary if public_id exists
      if (application.resumePublicId) {
        const deleteResult = await cloudinaryService.deleteResume(application.resumePublicId);
        if (!deleteResult.success) {
          console.warn('Failed to delete resume from Cloudinary:', deleteResult.error);
          // Continue with Firestore deletion even if Cloudinary deletion fails
        }
      }

      // Delete from Firestore
      await deleteDoc(doc(db, APPLICATIONS_COLLECTION, applicationId));
      return { success: true };

    } catch (error) {
      console.error('Error deleting application:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default applicationServices;